'use client';

import { HexString } from '#/components/ethereum/hex-string';
import { Avatar, AvatarFallback } from '#/components/ui/avatar';
import { Dialog } from '#/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '#/components/ui/dropdown-menu';
import {
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '#/components/ui/sidebar';
import { useLocaleStore } from '#/contexts/locale-store';
import useUserInfo from '#/data/useUserInfo';
import { AddAddressDialogContent } from '#/modules/dialogs/add-address-dialog';
import { LogoutDialog } from '#/modules/dialogs/logout-dialog';
import { StakingDialog } from '#/modules/dialogs/staking-dialog';
import getSsoProfileUrl from '#/utils/getSsoProfileUrl';
import { useAppKit } from '@reown/appkit/react';
import { LocaleId, Translation, useIntl } from 'intl-lib';
import {
  ChevronsUpDown,
  Languages,
  LogOut,
  PackagePlus,
  PlusCircle,
  User,
  Wallet,
} from 'lucide-react';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import { keccak256, toBytes } from 'viem';
import { useAccount, useConnect, useDisconnect } from 'wagmi';

const localeOptions = [
  {
    label: 'English',
    value: 'en-US',
  },
  {
    label: '简体中文',
    value: 'zh-CN',
  },
];

export function AppSidebarFooter() {
  const { address, chain, connector, isConnected } = useAccount();
  const modal = useAppKit();
  const { disconnect } = useDisconnect();
  const { connect } = useConnect();

  const { data, isLoading } = useUserInfo();
  const avatarFallback = useMemo(
    () => (data?.data.data?.name || data?.data.data?.email)?.charAt(0),
    [data]
  );
  const avatarColor = useMemo(
    () =>
      data?.data.data?.email != null
        ? `#${keccak256(toBytes(data?.data.data?.email ?? '')).substring(2, 8)}`
        : '#FFFFFF',
    [data]
  );

  const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);
  const [isStakingOpen, setIsStakingOpen] = useState(false);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);

  const locale = useIntl(state => state.locale);
  const setLocale = useLocaleStore(state => state.setLocale);

  return (
    <>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            {isConnected ? (
              <SidebarMenuButton
                className="h-9"
                onClick={() => modal.open({ view: 'Account' })}
              >
                <Wallet />
                <span>
                  <HexString value={address} diameter={16} />
                </span>
                <ChevronsUpDown className="ml-auto" />
              </SidebarMenuButton>
            ) : (
              <SidebarMenuButton
                className="h-9"
                onClick={() =>
                  modal.open({
                    view: 'Connect',
                  })
                }
              >
                <Wallet />
                <span>Connect Wallet</span>
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton className="h-9 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                  <Avatar className="size-4">
                    {/* <AvatarImage src={undefined} alt={data?.data.data?.email} /> */}
                    <AvatarFallback
                      className="uppercase bg-sidebar-primary"
                      style={{ color: avatarColor }}
                    >
                      {avatarFallback}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate">
                    {data?.data.data?.name || data?.data.data?.email}
                  </span>
                  <ChevronsUpDown className="ml-auto" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="top"
                className="w-(--radix-popper-anchor-width)"
              >
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <p className="text-sm">
                      {data?.data.data?.name}(ID: {data?.data.data?.userId})
                    </p>
                    <p className="text-sm text-muted-foreground font-normal">
                      {data?.data.data?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem asChild>
                    <Link
                      href={getSsoProfileUrl()}
                      target="_blank"
                      prefetch={false}
                    >
                      <User className="h-4 w-4" />
                      <span>
                        <Translation path="UserNav.EditProfile" />
                      </span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setIsAddAddressOpen(true)}>
                    <PlusCircle className="h-4 w-4" />
                    <Translation path="PortfolioDashboard.AddAddress.ButtonLabel" />
                    {/* <DropdownMenuShortcut>⌃⌥A</DropdownMenuShortcut> */}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setIsStakingOpen(true)}>
                    <PackagePlus className="h-4 w-4" />
                    <Translation path="my_staking.staking_dialog.trigger" />
                    {/* <DropdownMenuShortcut>⌃⌥N</DropdownMenuShortcut> */}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className="gap-2">
                      <Languages className="h-4 w-4 text-muted-foreground" />
                      {localeOptions.find(option => option.value === locale)
                        ?.label ?? 'Language'}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent>
                        <DropdownMenuRadioGroup
                          value={locale}
                          onValueChange={value => setLocale(value as LocaleId)}
                        >
                          {localeOptions.map(option => (
                            <DropdownMenuRadioItem
                              key={option.value}
                              value={option.value}
                            >
                              {option.label}
                            </DropdownMenuRadioItem>
                          ))}
                        </DropdownMenuRadioGroup>
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setIsLogoutOpen(true)}>
                  <LogOut className="h-4 w-4" />
                  <Translation path="UserNav.SignOut.ButtonLabel" />
                  {/* <DropdownMenuShortcut>⌃⌥Q</DropdownMenuShortcut> */}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <LogoutDialog open={isLogoutOpen} onOpenChange={setIsLogoutOpen} />
      <Dialog open={isAddAddressOpen} onOpenChange={setIsAddAddressOpen}>
        <AddAddressDialogContent />
      </Dialog>
      <StakingDialog open={isStakingOpen} onOpenChange={setIsStakingOpen} />
    </>
  );
}
